import {
  type ChangeEvent,
  Children,
  cloneElement,
  type DragEvent,
  forwardRef,
  Fragment,
  type ReactElement,
  type ReactNode,
  useImperativeHandle,
  useRef,
} from 'react'
import { makeStyles } from '@mui/styles'
import type { ClassesType } from '@pbt/pbt-ui-components'

const useStyles = makeStyles(
  () => ({
    file: {
      display: 'none',
    },
  }),
  { name: 'FileSelector' },
)

export interface FileSelectorProps {
  accept?: string
  children: ReactNode
  classes?: ClassesType<typeof useStyles>
  multiple?: boolean
  onDragOverChange?: (value: boolean) => void
  /** Fired when the user activates the file picker (e.g. click on trigger child), before the native dialog opens */
  onOpenPicker?: () => void
  onlyImages?: boolean
  processFiles: (
    event: ChangeEvent<HTMLInputElement> | DragEvent<HTMLInputElement>,
  ) => void
}
const FileSelector = forwardRef(function FileSelector(
  {
    accept,
    classes: classesProp,
    children,
    onDragOverChange,
    onOpenPicker,
    onlyImages,
    processFiles,
    multiple = false,
  }: FileSelectorProps,
  ref,
) {
  const classes = useStyles({ classes: classesProp })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    processFiles(event)
    event.target.value = ''
  }

  const onDragIn = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (onDragOverChange) {
      onDragOverChange(true)
    }
  }

  const onDragOut = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (onDragOverChange) {
      onDragOverChange(false)
    }
  }

  const onDrop = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault()
    onDragOut(event)
    processFiles(event)
  }

  const childrenArray = Children.toArray(children) as ReactElement[]

  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => inputRef?.current)

  const content = childrenArray.map((child, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Fragment key={index}>
      {cloneElement(child, {
        onClick: () => {
          onOpenPicker?.()
          if (inputRef.current) {
            inputRef.current.click()
          }
        },
        onDragEnter: onDragIn,
        onDragLeave: onDragOut,
        onDragOver: onDragIn,
        onDrop,
      })}
    </Fragment>
  ))

  const acceptFilter = accept ?? (onlyImages ? 'image/*' : undefined)

  return (
    <>
      <input
        accept={acceptFilter}
        className={classes.file}
        multiple={multiple}
        ref={inputRef}
        type="file"
        onChange={onChange}
      />
      {content}
    </>
  )
})

export default FileSelector
